import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { UploadZone } from '@/components/upload/UploadZone'
import { Clock, RefreshCw, Database, Info, CheckCircle, ExternalLink } from 'lucide-react'

export function UploadPage() {
  const { getToken } = useAuth()
  const [isUploading, setIsUploading] = useState(false)
  const [uploadComplete, setUploadComplete] = useState(false)
  const [showTooltip, setShowTooltip] = useState<string | null>(null)

  const handleUpload = async (file: File) => {
    setIsUploading(true)
    setUploadComplete(false)

    try {
      const token = await getToken()

      if (!token) {
        throw new Error('認証トークンの取得に失敗しました')
      }

      const formData = new FormData()
      formData.append('file', file)

      const res = await fetch('/api/v1/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })

      if (!res.ok) {
        throw new Error('Upload failed')
      }

      setUploadComplete(true)
    } catch (error) {
      console.error('Upload error:', error)
      alert('アップロードに失敗しました')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-slide-up">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-2">視聴履歴をアップロード</h2>
        <p className="text-muted-foreground">
          NetflixのCSVファイルをドラッグ&ドロップ
        </p>
      </div>

      {uploadComplete ? (
        /* Success State */
        <div className="border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-12 text-center shadow-lg">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-green-900 mb-3">アップロード完了</h3>
          <p className="text-green-700 mb-8 text-lg">
            バックグラウンドで処理中です
          </p>
          <a
            href="https://tkmmky.com/album"
            className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium shadow-lg hover:shadow-xl transition-all"
          >
            アルバムを見る
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      ) : (
        <>
          {/* Upload Zone */}
          <UploadZone onUpload={handleUpload} isUploading={isUploading} />

          {/* Info Cards - Compact with Tooltips */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Processing Time */}
            <div
              className="relative group"
              onMouseEnter={() => setShowTooltip('time')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              <div className="border rounded-xl p-4 bg-card hover:shadow-md transition-shadow cursor-help">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">処理時間</h4>
                    <p className="text-xs text-muted-foreground">数分〜数十分</p>
                  </div>
                </div>
                {showTooltip === 'time' && (
                  <div className="absolute z-10 w-64 p-3 mt-2 text-xs bg-gray-900 text-white rounded-lg shadow-xl">
                    アップロード後、バックグラウンドで処理が実行されます。タイトル数により数分〜数十分かかる場合があります。
                    <div className="absolute -top-1 left-6 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Differential Sync */}
            <div
              className="relative group"
              onMouseEnter={() => setShowTooltip('sync')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              <div className="border rounded-xl p-4 bg-card hover:shadow-md transition-shadow cursor-help">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <RefreshCw className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">差分同期</h4>
                    <p className="text-xs text-muted-foreground">重複なし</p>
                  </div>
                </div>
                {showTooltip === 'sync' && (
                  <div className="absolute z-10 w-64 p-3 mt-2 text-xs bg-gray-900 text-white rounded-lg shadow-xl">
                    既存のデータと新しいデータを差分で同期します。同じ視聴履歴を複数回アップロードしても重複しません。
                    <div className="absolute -top-1 left-6 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Unique Key */}
            <div
              className="relative group"
              onMouseEnter={() => setShowTooltip('unique')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              <div className="border rounded-xl p-4 bg-card hover:shadow-md transition-shadow cursor-help">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Database className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">ユニーク判定</h4>
                    <p className="text-xs text-muted-foreground">タイトル + 日付</p>
                  </div>
                </div>
                {showTooltip === 'unique' && (
                  <div className="absolute z-10 w-64 p-3 mt-2 text-xs bg-gray-900 text-white rounded-lg shadow-xl">
                    視聴履歴は以下の組み合わせで一意に識別されます：
                    <ul className="mt-2 space-y-1">
                      <li>• タイトル名（コロンより前）</li>
                      <li>• 視聴日</li>
                    </ul>
                    <div className="absolute -top-1 left-6 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Download Instructions - Collapsible */}
          <details className="group border rounded-xl overflow-hidden bg-card shadow-sm">
            <summary className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors">
              <Info className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <span className="font-medium">視聴履歴のダウンロード方法</span>
              <svg
                className="ml-auto h-5 w-5 text-muted-foreground group-open:rotate-180 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="px-4 pb-4 pt-2 border-t">
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>
                  <a
                    href="https://www.netflix.com/account"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium inline-flex items-center gap-1"
                  >
                    Netflixアカウント設定
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  にアクセス
                </li>
                <li>プロフィールを選択 → 「視聴履歴」をクリック</li>
                <li>ページ下部の「すべてダウンロード」をクリック</li>
                <li>ダウンロードしたCSVファイルを上記にアップロード</li>
              </ol>
            </div>
          </details>
        </>
      )}
    </div>
  )
}
